import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useLatestRef } from "@/hooks/useLatestRef";
interface IntegrationOAuthResultMessage {
  type: string;
  success?: boolean;
  error?: string | null;
  /** True when the callback page chained the next OAuth leg server-side. */
  chained?: boolean;
  [key: string]: unknown;
}
export interface IntegrationOAuthChainOptions {
  /**
   * Returns the URL of the chained (second) OAuth leg, called at the moment
   * the first leg succeeds. Return null/undefined to skip chaining — e.g.
   * the user's account is already linked, or the link URL hasn't loaded.
   */
  getChainUrl: () => string | null | undefined;
  /**
   * Query keys to invalidate when the chained leg delivers a result or its
   * popup closes.
   */
  queryKeysToInvalidate: string[][];
  /** Fires once the popup has been navigated to the chained URL. */
  onStart?: () => void;
  onSuccess?: (message: IntegrationOAuthResultMessage) => void;
  onError?: (reason: string | null, message: IntegrationOAuthResultMessage) => void;
  /**
   * Fires when the popup closes during the chained leg without delivering a
   * result message (e.g. the user dismisses the second OAuth screen).
   */
  onClose?: () => void;
}
interface UseIntegrationOAuthPopupOptions {
  /** Unique window name so multiple hook instances don't share a popup */
  popupName: string;
  /** postMessage `type` emitted by the OAuth callback HTML */
  resultType: string;
  queryKeysToInvalidate: string[][];
  onSuccess?: (message: IntegrationOAuthResultMessage) => void;
  onError?: (reason: string | null, message: IntegrationOAuthResultMessage) => void;
  /**
   * Fires when the popup/tab is closed without delivering a result message
   * (e.g. the user dismisses the OAuth window). Lets callers reset any
   * "connecting" UI state that `onSuccess`/`onError` would otherwise own.
   */
  onClose?: () => void;
  /** Fires when both the popup and the new-tab fallback are blocked. */
  onBlocked?: () => void;
  /**
   * Optional second OAuth leg chained into the same popup window (e.g.
   * user-account linking immediately after a workspace install). Preferred
   * path: the backend's callback page redirects the popup to the second leg
   * itself and marks its result message `chained: true`, which works even
   * when the provider's Cross-Origin-Opener-Policy severed `window.opener`
   * and no message ever reaches us. When the first leg succeeds via
   * postMessage without the server-side chain, the still-open popup — the
   * callback page only closes itself after a short delay — is navigated
   * straight to `getChainUrl()`. Navigating an existing window is not
   * subject to the popup blocker (unlike `window.open` from a message
   * handler, which has no user activation) and discards the callback page's
   * pending `window.close()` timer. Either way, the next result message is
   * routed to the chain callbacks instead of the first leg's. If the chain
   * can't start (popup already closed, result arrived via close-polling, or
   * no chain URL available), callers should fall back to a click-driven
   * flow.
   */
  chain?: IntegrationOAuthChainOptions;
}

// Origin of the backend API — the OAuth callback HTML is served from this domain
const API_ORIGIN = process.env.NEXT_PUBLIC_API_URL ? new URL(process.env.NEXT_PUBLIC_API_URL).origin : "";
export function useIntegrationOAuthPopup(t0) {
  const $ = _c(30);
  const {
    popupName,
    resultType,
    queryKeysToInvalidate,
    onSuccess,
    onError,
    onClose,
    onBlocked,
    chain
  } = t0;
  const queryClient = useQueryClient();
  const popupRef = useRef(null);
  const pollingRef = useRef(null);
  const focusListenerRef = useRef(null);
  const queryKeysRef = useLatestRef(queryKeysToInvalidate);
  const onSuccessRef = useLatestRef(onSuccess);
  const onErrorRef = useLatestRef(onError);
  const onCloseRef = useLatestRef(onClose);
  const onBlockedRef = useLatestRef(onBlocked);
  const chainRef = useLatestRef(chain);
  const chainActiveRef = useRef(false);
  let t1;
  if ($[0] !== chainRef || $[1] !== queryClient || $[2] !== queryKeysRef) {
    t1 = () => {
      for (const key of queryKeysRef.current) {
        queryClient.invalidateQueries(key);
      }
      if (chainActiveRef.current) {
        for (const key_0 of chainRef.current?.queryKeysToInvalidate ?? []) {
          queryClient.invalidateQueries(key_0);
        }
      }
    };
    $[0] = chainRef;
    $[1] = queryClient;
    $[2] = queryKeysRef;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const invalidateQueries = t1;
  let t2;
  if ($[4] !== chainRef || $[5] !== queryClient) {
    t2 = () => {
      for (const key_1 of chainRef.current?.queryKeysToInvalidate ?? []) {
        queryClient.invalidateQueries(key_1);
      }
    };
    $[4] = chainRef;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const invalidateChainQueries = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (focusListenerRef.current) {
        window.removeEventListener("focus", focusListenerRef.current);
        focusListenerRef.current = null;
      }
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const stopPolling = t3;
  let t4;
  if ($[8] !== chainRef || $[9] !== invalidateChainQueries || $[10] !== invalidateQueries || $[11] !== onCloseRef) {
    t4 = () => {
      const wasChained = chainActiveRef.current;
      chainActiveRef.current = false;
      stopPolling();
      popupRef.current = null;
      if (wasChained) {
        invalidateChainQueries();
        chainRef.current?.onClose?.();
      } else {
        invalidateQueries();
        onCloseRef.current?.();
      }
    };
    $[8] = chainRef;
    $[9] = invalidateChainQueries;
    $[10] = invalidateQueries;
    $[11] = onCloseRef;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handlePopupClosed = t4;
  let t5;
  let t6;
  if ($[13] !== chainRef || $[14] !== invalidateChainQueries || $[15] !== invalidateQueries || $[16] !== onErrorRef || $[17] !== onSuccessRef || $[18] !== resultType) {
    t5 = () => {
      const handleMessage = event => {
        if (event.origin !== API_ORIGIN) {
          return;
        }
        if (event.data?.type !== resultType) {
          return;
        }
        if (!popupRef.current || event.source !== popupRef.current) {
          return;
        }
        const message = event.data as IntegrationOAuthResultMessage;
        if (chainActiveRef.current) {
          chainActiveRef.current = false;
          popupRef.current = null;
          stopPolling();
          invalidateChainQueries();
          if (message.success) {
            chainRef.current?.onSuccess?.(message);
          } else {
            chainRef.current?.onError?.(message.error ?? null, message);
          }
          return;
        }
        invalidateQueries();
        let chained = false;
        if (message.success) {
          if (message.chained === true) {
            chained = true;
          } else {
            if (chainRef.current) {
              const chainUrl = chainRef.current.getChainUrl();
              if (chainUrl && !popupRef.current.closed) {
                try {
                  popupRef.current.location.href = chainUrl;
                  chained = true;
                } catch {}
              }
            }
          }
        }
        if (chained) {
          chainActiveRef.current = true;
          chainRef.current?.onStart?.();
        } else {
          popupRef.current = null;
          stopPolling();
        }
        if (message.success) {
          onSuccessRef.current?.(message);
        } else {
          onErrorRef.current?.(message.error ?? null, message);
        }
      };
      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    };
    t6 = [chainRef, invalidateChainQueries, invalidateQueries, onErrorRef, onSuccessRef, resultType, stopPolling];
    $[13] = chainRef;
    $[14] = invalidateChainQueries;
    $[15] = invalidateQueries;
    $[16] = onErrorRef;
    $[17] = onSuccessRef;
    $[18] = resultType;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      stopPolling();
    };
    t8 = [stopPolling];
    $[21] = t7;
    $[22] = t8;
  } else {
    t7 = $[21];
    t8 = $[22];
  }
  useEffect(t7, t8);
  let t9;
  if ($[23] !== handlePopupClosed || $[24] !== invalidateQueries || $[25] !== onBlockedRef || $[26] !== popupName) {
    t9 = url => {
      stopPolling();
      chainActiveRef.current = false;
      const left = window.screenX + (window.outerWidth - 600) / 2;
      const top = window.screenY + (window.outerHeight - 700) / 2;
      const popup = window.open(url, popupName, `width=${600},height=${700},left=${left},top=${top},popup=yes`);
      if (!popup) {
        const tab = window.open(url, "_blank");
        if (!tab) {
          onBlockedRef.current?.();
          return false;
        }
        popupRef.current = tab;
        pollingRef.current = setInterval(() => {
          invalidateQueries();
          if (popupRef.current?.closed) {
            handlePopupClosed();
          }
        }, 3000);
        const onFocus = () => invalidateQueries();
        focusListenerRef.current = onFocus;
        window.addEventListener("focus", onFocus);
        return true;
      }
      popupRef.current = popup;
      pollingRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          handlePopupClosed();
        }
      }, 500);
      return true;
    };
    $[23] = handlePopupClosed;
    $[24] = invalidateQueries;
    $[25] = onBlockedRef;
    $[26] = popupName;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const openPopup = t9;
  let t10;
  if ($[28] !== openPopup) {
    t10 = {
      openPopup
    };
    $[28] = openPopup;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}